import { useCallback, useMemo } from 'react';
import { PaymentMethod, PaymentPlanFrequency } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { ButtonProps, useFormField } from '@frontend/design-system';
import { PaymentMethodsProps } from '../../payment-methods';
import { PaymentMethodsView } from '../types/provider.types';
import { useDeletePaymentMethod } from './use-delete-payment-method';
import { usePaymentMethodsDerivedState } from './use-payment-methods-derived-state';
import { PaymentMethodsStripeApi } from './use-payment-methods-stripe-api';

const MIN_PAYMENT_PLAN_ACH_AMOUNT = 2500;

export type UsePaymentMethodsStripeProps = Omit<PaymentMethodsProps, 'children'> & {
  api: PaymentMethodsStripeApi;
  paymentsUrl: string | undefined;
  locationId: string | undefined;
  selectedPaymentMethod: PaymentMethod | null;
  enableAchOnFile: boolean;
  enableAddAchMethod: boolean;
  onSelectPaymentMethod: (paymentMethod: PaymentMethod | null) => void;
  onVerificationEmailSent?: () => void;
  onChangeView: (view: PaymentMethodsView) => void;
  onChangeResendVerificationAchMode: (isResendVerificationAchMode: boolean) => void;
};
export const usePaymentMethodsStripe = ({
  onError,
  onPaymentMethodSelectedSuccess, // final success callback
  onVerificationEmailSent,
  onSelectPaymentMethod, // set selected
  onChangeView,
  onCancel,
  onChangeResendVerificationAchMode,
  showCancelButtonOnList,
  selectedPaymentMethod,
  api,
  amount: paymentAmount,
  personId,
  person: propsPerson,
  paymentsUrl,
  locationId,
  enableAchOnFile,
  enableAddAchMethod,
  frequency = {},
  invoiceId,
}: UsePaymentMethodsStripeProps) => {
  const { t } = useTranslation('payments');

  const {
    person,
    paymentMethods,
    defaultPaymentMethod,
    isLoadingPaymentMethods,
    isRefetchingPaymentMethods,
    refetchPaymentMethodsAsync,
    authorizationEmail,
    personEmail,
  } = usePaymentMethodsDerivedState({
    personId,
    person: propsPerson,
    locationId,
    paymentsUrl,
    useGetPaymentMethodsByPerson: api.useGetPaymentMethodsByPerson,
    useQueryLegacyPerson: api.useQueryLegacyPerson,
  });

  const { cardPaymentMethods, achPaymentMethods } = useMemo(
    () => ({
      cardPaymentMethods: (paymentMethods || []).filter(({ type }) => type === 'card'),
      achPaymentMethods: (paymentMethods || []).filter(({ type }) => type === 'us_bank_account'),
    }),
    [paymentMethods]
  );

  const shouldShowFooter = !!person?.personId;

  const shouldShowLoading = !person?.personId || isLoadingPaymentMethods || isRefetchingPaymentMethods;

  const refetchPaymentMethods = useCallback(async () => {
    await refetchPaymentMethodsAsync();
  }, [refetchPaymentMethodsAsync]);

  const showAchList = enableAchOnFile && (shouldShowLoading || !!achPaymentMethods.length || enableAddAchMethod);

  const { isDeleting, ConFirmDeleteModal, showConfirmDeletePaymentModal, resetPaymentMethodToDelete } =
    useDeletePaymentMethod({
      paymentsUrl,
      locationId,
      personId: person?.personId,
      api,
    });

  const invalidAmount = !!paymentAmount && paymentAmount < MIN_PAYMENT_PLAN_ACH_AMOUNT;

  const { oneTimePayment, paymentFrequency } = frequency;

  const achDisabledByCadence = !oneTimePayment && paymentFrequency === PaymentPlanFrequency.Weekly;
  const disableAchPaymentMethods = invalidAmount || achDisabledByCadence;

  const onClickResendVerification = useCallback(() => {
    onChangeResendVerificationAchMode(true);
    onChangeView('add-ach');
  }, [onChangeResendVerificationAchMode, onChangeView]);

  /**
   * Add ACH method
   */

  const fieldProps = useFormField({ type: 'email', required: true, value: authorizationEmail || personEmail });

  const { mutateAsync: createRegistrationLink, isLoading: isCreatingRegistrationLink } =
    api.useCreatePMRegistrationLink({
      onSuccess: () => {
        refetchPaymentMethodsAsync();
        onVerificationEmailSent?.();
        onChangeView('list');
      },
      onError: () => {
        onError?.(t('Failed to send ACH verification'));
      },
    });

  const sendAchVerification = useCallback(() => {
    if (fieldProps.error || !person?.personId) {
      console.warn('Error sending ACH verification. Invalid email or personId.');
      return;
    } else if (!paymentsUrl) {
      console.error('Error sending ACH verification. No location selected.');
      return;
    } else if (!api.canUserAccessAch(paymentsUrl)) {
      onError?.(t('Weave users do not have access to save ACH.'));
      return;
    }

    return createRegistrationLink({ email: fieldProps.value, personId: person?.personId, locationId, paymentsUrl });
  }, [fieldProps.error, fieldProps.value, paymentsUrl, onError, person?.personId, locationId, createRegistrationLink]);

  const listPrimary = useMemo<ButtonProps>(() => {
    return {
      label: t('Save'),
      variant: 'primary',
      disabled: !selectedPaymentMethod,
      onClick: () => {
        if (!selectedPaymentMethod) {
          onError?.('No payment method selected');
          return;
        }
        onPaymentMethodSelectedSuccess(selectedPaymentMethod);
      },
    };
  }, [selectedPaymentMethod, onError, onPaymentMethodSelectedSuccess]);

  const listSecondary = useMemo<ButtonProps | undefined>(() => {
    if (!showCancelButtonOnList) {
      return undefined;
    }
    return {
      label: t('Cancel'),
      variant: 'secondary',
      onClick: () => {
        onCancel?.();
      },
    };
  }, [onCancel, showCancelButtonOnList]);

  const achPrimary = useMemo<ButtonProps>(() => {
    return {
      label: t('Send ACH Verification'),
      variant: 'primary',
      loading: isCreatingRegistrationLink,
      disabled: fieldProps['aria-invalid'] || !fieldProps.value,
      onClick: () => sendAchVerification(),
    };
  }, [sendAchVerification, fieldProps, isCreatingRegistrationLink]);

  const achSecondary = useMemo<ButtonProps>(() => {
    return {
      label: t('Cancel'),
      variant: 'secondary',
      disabled: isCreatingRegistrationLink,
      onClick: () => {
        onChangeView('list');
      },
    };
  }, [onCancel, onSelectPaymentMethod, isCreatingRegistrationLink]);

  const onClickAddCard = useCallback(() => {
    onChangeView('add-card');
  }, [onChangeView]);

  const onClickAddAch = useCallback(() => {
    onChangeView('add-ach');
  }, [onChangeView]);

  const value = {
    person,
    achPrimary,
    achSecondary,
    listPrimary,
    listSecondary,
    shouldShowFooter,
    shouldShowLoading,
    defaultPaymentMethod,
    cardPaymentMethods,
    achPaymentMethods,
    paymentMethods: paymentMethods || [],
    showAchList,
    isDeleting,
    ConFirmDeleteModal,
    minAchAmount: MIN_PAYMENT_PLAN_ACH_AMOUNT,
    amount: paymentAmount,
    frequency,
    disableAchPaymentMethods,
    invoiceId,
    authorizationEmail,
    isCreatingRegistrationLink,
    showCancelButtonOnList,
    fieldProps,
    onError,
    onCancel,
    onClickAddAch,
    onClickAddCard,
    showConfirmDeletePaymentModal,
    refetchPaymentMethods,
    resetPaymentMethodToDelete,
    onClickResendVerification,
    onSelectPaymentMethod,
    onPaymentMethodSelectedSuccess,
    sendAchVerification,
    invalidatePaymentMethodsByPerson: api.invalidatePaymentMethodsByPerson,
  };

  return value;
};

export type UsePaymentMethodsStripe = ReturnType<typeof usePaymentMethodsStripe>;
